import type { Table } from 'dexie';
import { db, METADATA_ID, SETTINGS_ID, APP_VERSION, DATABASE_SCHEMA_VERSION, type TrainingDatabase } from '../database';
import type {
  AppSettings,
  DatabaseSnapshot,
  Exercise,
  LocalDate,
  MuscleGroup,
  PlanExercise,
  PlanExerciseDraft,
  PlanWithExercises,
  ScheduledWorkout,
  SetEntry,
  SnapshotTableName,
  WorkoutExercise,
  WorkoutPlan,
  WorkoutSession,
  WorkoutWithDetails
} from '../types';
import { UserFacingError } from '../../shared/errors';
import { compareLocalDates, nowIso, toLocalDate } from '../../shared/utils/date';
import { displayExerciseName, isValidRestDuration, normalizeExerciseName, parseRepetitions, parseWeight } from '../../shared/utils/number';
import { makeId } from '../../shared/utils/id';
import { withWriteLock } from './writeLock';
import { exerciseWarning } from '../../services/coaching/catalog';
import { appSettingsSchema, databaseSnapshotSchema } from '../../shared/validation/schemas';
import { startedRestPatch } from '../../services/rest-timer/restTimer';

export interface SavePlanInput {
  id?: string;
  name: string;
  notes: string | null;
  exercises: PlanExerciseDraft[];
}

export interface CreateScheduleInput {
  id?: string;
  scheduledDate: LocalDate;
  reminderTime: string;
  planId: string;
}

export interface MergeReport {
  added: number;
  updated: number;
  skipped: number;
}

const snapshotTableNames: SnapshotTableName[] = [
  'exercises', 'workoutPlans', 'planExercises', 'workoutSessions', 'workoutExercises',
  'setEntries', 'scheduledWorkouts', 'appSettings', 'appMetadata', 'userProfiles', 'exercisePreferences', 'bodyMeasurements'
];

export class TrainingRepository {
  constructor(readonly database: TrainingDatabase = db) {}

  async settings(): Promise<AppSettings> {
    const settings = await this.database.appSettings.get(SETTINGS_ID);
    if (!settings) throw new UserFacingError('Настройки не найдены. Перезапустите приложение.');
    return settings;
  }

  async updateSettings(patch: Partial<Omit<AppSettings, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> {
    if (patch.defaultRestSeconds !== undefined && !isValidRestDuration(patch.defaultRestSeconds)) {
      throw new UserFacingError('Время отдыха должно быть от 15 до 900 секунд с шагом 15 секунд.');
    }
    const updatedAt = nowIso();
    appSettingsSchema.parse({ ...await this.settings(), ...patch, updatedAt });
    await this.database.appSettings.update(SETTINGS_ID, { ...patch, updatedAt });
  }

  async allExercises(includeArchived = false): Promise<Exercise[]> {
    const items = await this.database.exercises.orderBy('name').toArray();
    return includeArchived ? items : items.filter(item => !item.isArchived);
  }

  async createExercise(name: string, muscleGroup: MuscleGroup): Promise<Exercise> {
    const displayName = displayExerciseName(name);
    const normalizedName = normalizeExerciseName(displayName);
    if (!normalizedName) throw new UserFacingError('Введите название упражнения.');
    const duplicate = await this.database.exercises.where('normalizedName').equals(normalizedName).first();
    if (duplicate) throw new UserFacingError(`Упражнение «${duplicate.name}» уже есть в каталоге.`);
    const timestamp = nowIso();
    const exercise: Exercise = {
      id: makeId(), name: displayName, normalizedName, muscleGroup, isCustom: true, isArchived: false,
      createdAt: timestamp, updatedAt: timestamp
    };
    await this.database.exercises.add(exercise);
    return exercise;
  }

  async updateExercise(id: string, name: string, muscleGroup: MuscleGroup, isArchived: boolean): Promise<void> {
    const exercise = await this.database.exercises.get(id);
    if (!exercise) throw new UserFacingError('Упражнение больше не найдено.');
    if (!exercise.isCustom) throw new UserFacingError('Базовые упражнения нельзя изменять.');
    const displayName = displayExerciseName(name);
    const normalizedName = normalizeExerciseName(displayName);
    if (!normalizedName) throw new UserFacingError('Введите название упражнения.');
    const duplicate = await this.database.exercises.where('normalizedName').equals(normalizedName).first();
    if (duplicate && duplicate.id !== id) throw new UserFacingError(`Упражнение «${duplicate.name}» уже есть в каталоге.`);
    await this.database.exercises.update(id, { name: displayName, normalizedName, muscleGroup, isArchived, updatedAt: nowIso() });
  }

  async plans(includeArchived = false): Promise<WorkoutPlan[]> {
    const items = await this.database.workoutPlans.orderBy('updatedAt').reverse().toArray();
    return includeArchived ? items : items.filter(item => !item.isArchived);
  }

  async plan(id: string): Promise<PlanWithExercises | null> {
    const plan = await this.database.workoutPlans.get(id);
    if (!plan) return null;
    const items = await this.database.planExercises.where('planId').equals(id).sortBy('orderIndex');
    const exerciseMap = new Map((await this.database.exercises.bulkGet(items.map(item => item.exerciseId))).filter(Boolean).map(item => [item!.id, item!]));
    return { ...plan, exercises: items.map(item => ({ ...item, exercise: exerciseMap.get(item.exerciseId) ?? null })) };
  }

  async savePlan(input: SavePlanInput): Promise<WorkoutPlan> {
    const name = displayExerciseName(input.name);
    if (!name) throw new UserFacingError('Введите название плана.');
    this.validatePlanDrafts(input.exercises);
    const timestamp = nowIso();
    return this.database.transaction('rw', this.database.workoutPlans, this.database.planExercises, this.database.exercises, async () => {
      const exercises = await this.database.exercises.bulkGet(input.exercises.map(item => item.exerciseId));
      if (exercises.some(item => !item)) throw new UserFacingError('Одно из упражнений больше не найдено.');

      const existing = input.id ? await this.database.workoutPlans.get(input.id) : undefined;
      const plan: WorkoutPlan = existing ? {
        ...existing, name, notes: input.notes?.trim() || null, updatedAt: timestamp
      } : {
        id: makeId(), name, notes: input.notes?.trim() || null, isArchived: false,
        createdAt: timestamp, updatedAt: timestamp
      };
      await this.database.workoutPlans.put(plan);
      if (existing) await this.database.planExercises.where('planId').equals(plan.id).delete();
      const planExercises: PlanExercise[] = input.exercises.map((item, orderIndex) => ({
        id: item.id ?? makeId(), planId: plan.id, exerciseId: item.exerciseId, orderIndex,
        targetSets: item.targetSets, targetRepsMin: item.targetRepsMin, targetRepsMax: item.targetRepsMax,
        targetWeightKg: item.targetWeightKg === null ? null : Math.round(item.targetWeightKg * 100) / 100,
        notes: item.notes?.trim() || null, createdAt: timestamp, updatedAt: timestamp,
        ...(item.priority ? { priority: item.priority } : {})
      }));
      if (planExercises.length > 0) await this.database.planExercises.bulkPut(planExercises);
      return plan;
    });
  }

  async duplicatePlan(id: string): Promise<WorkoutPlan> {
    const source = await this.plan(id);
    if (!source) throw new UserFacingError('План больше не найден.');
    return this.savePlan({
      name: `${source.name} — копия`, notes: source.notes,
      exercises: source.exercises.map(item => ({
        exerciseId: item.exerciseId, targetSets: item.targetSets, targetRepsMin: item.targetRepsMin,
        targetRepsMax: item.targetRepsMax, targetWeightKg: item.targetWeightKg, notes: item.notes, ...(item.priority ? { priority: item.priority } : {})
      }))
    });
  }

  async setPlanArchived(id: string, isArchived: boolean): Promise<void> {
    await this.database.workoutPlans.update(id, { isArchived, updatedAt: nowIso() });
  }

  async sessions(): Promise<WorkoutSession[]> {
    return this.database.workoutSessions.orderBy('workoutDate').reverse().toArray();
  }

  async sessionsOn(date: LocalDate): Promise<WorkoutSession[]> {
    return (await this.database.workoutSessions.where('workoutDate').equals(date).toArray())
      .filter(item => item.status === 'completed')
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  async activeSession(): Promise<WorkoutSession | null> {
    return (await this.database.workoutSessions.where('status').equals('active').first()) ?? null;
  }

  async workout(id: string): Promise<WorkoutWithDetails | null> {
    const session = await this.database.workoutSessions.get(id);
    if (!session) return null;
    const exercises = await this.database.workoutExercises.where('workoutSessionId').equals(id).sortBy('orderIndex');
    const sets = await this.database.setEntries.where('workoutExerciseId').anyOf(exercises.map(item => item.id)).toArray();
    const grouped = Map.groupBy(sets, item => item.workoutExerciseId);
    return {
      ...session,
      exercises: exercises.map(item => ({
        ...item,
        sets: (grouped.get(item.id) ?? []).toSorted((a, b) => a.orderIndex - b.orderIndex)
      }))
    };
  }

  async startWorkout(workoutDate: LocalDate, planId: string | null, availableMinutes: 40 | 60 | 90 = 60): Promise<WorkoutSession> {
    return withWriteLock('active-workout', () => this.database.transaction(
      'rw', [this.database.workoutSessions, this.database.workoutExercises, this.database.workoutPlans,
        this.database.planExercises, this.database.exercises, this.database.setEntries, this.database.appSettings, this.database.userProfiles],
      async () => this.createSession(workoutDate, planId, false, availableMinutes)
    ));
  }

  async startScheduledWorkout(id: string): Promise<WorkoutSession> {
    return withWriteLock('active-workout', () => this.database.transaction(
      'rw', [this.database.scheduledWorkouts, this.database.workoutSessions, this.database.workoutExercises,
        this.database.workoutPlans, this.database.planExercises, this.database.exercises, this.database.setEntries,
        this.database.appSettings, this.database.userProfiles],
      async () => {
        const schedule = await this.database.scheduledWorkouts.get(id);
        if (!schedule || schedule.status !== 'scheduled' || schedule.linkedWorkoutSessionId) {
          throw new UserFacingError('Эта запланированная тренировка уже была запущена.');
        }
        const session = await this.createSession(toLocalDate(new Date()), schedule.planId, true);
        const timestamp = nowIso();
        await this.database.scheduledWorkouts.update(id, {
          status: 'started', linkedWorkoutSessionId: session.id, updatedAt: timestamp
        });
        return session;
      }
    ));
  }

  private async createSession(workoutDate: LocalDate, planId: string | null, forceActive = false, availableMinutes: 40 | 60 | 90 = 60): Promise<WorkoutSession> {
    const today = toLocalDate(new Date());
    const historical = !forceActive && compareLocalDates(workoutDate, today) < 0;
    if (!historical && await this.database.workoutSessions.where('status').equals('active').count() > 0) {
      throw new UserFacingError('Сначала завершите или удалите текущую тренировку.');
    }
    const plan = planId ? await this.database.workoutPlans.get(planId) : undefined;
    if (planId && !plan) throw new UserFacingError('План недоступен. Выберите другой план.');
    const timestamp = nowIso();
    const profile = await this.database.userProfiles.toCollection().first();
    const session: WorkoutSession = {
      id: makeId(), workoutDate, startedAt: historical ? null : timestamp, finishedAt: historical ? timestamp : null,
      status: historical ? 'completed' : 'active', sourcePlanId: plan?.id ?? null,
      planNameSnapshot: plan?.name ?? null, notes: null, restEndsAt: null, restPausedSeconds: null,
      restSelectedSeconds: (await this.settings()).defaultRestSeconds, createdAt: timestamp, updatedAt: timestamp, availableMinutes
    };
    await this.database.workoutSessions.add(session);
    if (plan) {
      const allPlanItems = await this.database.planExercises.where('planId').equals(plan.id).sortBy('orderIndex');
      const planItems = allPlanItems.filter(item => historical || !item.priority || item.priority === 'core' ||
        (item.priority === 'additional' && availableMinutes >= 60) || availableMinutes >= 90);
      const exercises = await this.database.exercises.bulkGet(planItems.map(item => item.exerciseId));
      const workoutItems = planItems.flatMap((item, index): WorkoutExercise[] => {
        const exercise = exercises[index];
        if (!exercise) return [];
        if (!historical && plan.templateKey && exerciseWarning(exercise, profile ?? null)) return [];
        return [{
          id: makeId(), workoutSessionId: session.id, exerciseId: exercise.id,
          exerciseIdSnapshot: exercise.id, exerciseNameSnapshot: exercise.name,
          muscleGroupSnapshot: exercise.muscleGroup, targetSetsSnapshot: item.targetSets,
          targetRepsMinSnapshot: item.targetRepsMin, targetRepsMaxSnapshot: item.targetRepsMax,
          targetWeightKgSnapshot: item.targetWeightKg, orderIndex: index, notes: item.notes,
          createdAt: timestamp, updatedAt: timestamp
        }];
      });
      if (workoutItems.length > 0) {
        await this.database.workoutExercises.bulkAdd(workoutItems);
        const plannedSets = workoutItems.flatMap(workoutExercise => Array.from(
          { length: workoutExercise.targetSetsSnapshot ?? 0 },
          (_, orderIndex): SetEntry => ({
            id: makeId(), workoutExerciseId: workoutExercise.id, orderIndex,
            repetitions: null, weightKg: null, isCompleted: false, completedAt: null,
            createdAt: timestamp, updatedAt: timestamp
          })
        ));
        if (plannedSets.length > 0) await this.database.setEntries.bulkAdd(plannedSets);
      }
    }
    return session;
  }

  async addExerciseToWorkout(sessionId: string, exerciseId: string): Promise<WorkoutExercise> {
    return this.database.transaction('rw', this.database.workoutSessions, this.database.workoutExercises, this.database.exercises, async () => {
      const [session, exercise] = await Promise.all([
        this.database.workoutSessions.get(sessionId), this.database.exercises.get(exerciseId)
      ]);
      if (!session || !exercise) throw new UserFacingError('Тренировка или упражнение больше не найдено.');
      const existing = await this.database.workoutExercises.where('workoutSessionId').equals(sessionId).toArray();
      const timestamp = nowIso();
      const item: WorkoutExercise = {
        id: makeId(), workoutSessionId: sessionId, exerciseId: exercise.id,
        exerciseIdSnapshot: exercise.id, exerciseNameSnapshot: exercise.name,
        muscleGroupSnapshot: exercise.muscleGroup, targetSetsSnapshot: null, targetRepsMinSnapshot: null,
        targetRepsMaxSnapshot: null, targetWeightKgSnapshot: null,
        orderIndex: Math.max(-1, ...existing.map(item => item.orderIndex)) + 1,
        notes: null, createdAt: timestamp, updatedAt: timestamp
      };
      await this.database.workoutExercises.add(item);
      await this.database.workoutSessions.update(sessionId, { updatedAt: timestamp });
      return item;
    });
  }

  async removeWorkoutExercise(id: string): Promise<void> {
    await this.database.transaction('rw', this.database.workoutExercises, this.database.setEntries, async () => {
      await this.database.setEntries.where('workoutExerciseId').equals(id).delete();
      await this.database.workoutExercises.delete(id);
    });
  }

  async addSet(workoutExerciseId: string): Promise<SetEntry> {
    return this.database.transaction('rw', this.database.setEntries, this.database.workoutExercises, this.database.workoutSessions, async () => {
      const exercise = await this.database.workoutExercises.get(workoutExerciseId);
      if (!exercise) throw new UserFacingError('Упражнение больше не найдено.');
      const existing = await this.database.setEntries.where('workoutExerciseId').equals(workoutExerciseId).sortBy('orderIndex');
      const previous = existing.at(-1) ?? (await this.previousCompletedSets(exercise.exerciseIdSnapshot, exercise.workoutSessionId)).at(-1);
      const timestamp = nowIso();
      const entry: SetEntry = {
        id: makeId(), workoutExerciseId, orderIndex: Math.max(-1, ...existing.map(item => item.orderIndex)) + 1,
        repetitions: previous?.repetitions ?? null, weightKg: previous?.weightKg ?? null,
        isCompleted: false, completedAt: null, createdAt: timestamp, updatedAt: timestamp
      };
      await this.database.setEntries.add(entry);
      await this.touchWorkoutExercise(exercise, timestamp);
      return entry;
    });
  }

  async updateSet(id: string, weightText: string, repetitionsText: string): Promise<void> {
    const weightKg = parseWeight(weightText);
    const repetitions = parseRepetitions(repetitionsText);
    const entry = await this.database.setEntries.get(id);
    if (!entry) throw new UserFacingError('Подход больше не найден.');
    if (entry.isCompleted && (!repetitions || repetitions <= 0)) {
      throw new UserFacingError('Для выполненного подхода укажите повторения больше нуля.');
    }
    const timestamp = nowIso();
    await this.database.transaction('rw', this.database.setEntries, this.database.workoutExercises, this.database.workoutSessions, async () => {
      await this.database.setEntries.update(id, { weightKg, repetitions, updatedAt: timestamp });
      const exercise = await this.database.workoutExercises.get(entry.workoutExerciseId);
      if (exercise) await this.touchWorkoutExercise(exercise, timestamp);
    });
  }

  async setCompleted(id: string, completed: boolean): Promise<void> {
    await this.database.transaction('rw', this.database.setEntries, this.database.workoutExercises, this.database.workoutSessions, async () => {
      const entry = await this.database.setEntries.get(id);
      if (!entry) throw new UserFacingError('Подход больше не найден.');
      if (completed && (!entry.repetitions || entry.repetitions <= 0)) {
        throw new UserFacingError('Укажите хотя бы одно повторение.');
      }
      const timestamp = nowIso();
      await this.database.setEntries.update(id, { isCompleted: completed, completedAt: completed ? timestamp : null, updatedAt: timestamp });
      const exercise = await this.database.workoutExercises.get(entry.workoutExerciseId);
      if (exercise) {
        await this.touchWorkoutExercise(exercise, timestamp);
        if (completed && entry.kind !== 'warmup') {
          const session = await this.database.workoutSessions.get(exercise.workoutSessionId);
          if (session?.status === 'active') await this.database.workoutSessions.update(session.id, {
            ...startedRestPatch(session.restSelectedSeconds), updatedAt: timestamp
          });
        }
      }
    });
  }

  async deleteSet(id: string): Promise<void> {
    await this.database.setEntries.delete(id);
  }

  async updateWorkoutExerciseNotes(id: string, notes: string): Promise<void> {
    const exercise = await this.database.workoutExercises.get(id);
    if (!exercise) throw new UserFacingError('Упражнение больше не найдено.');
    const timestamp = nowIso();
    await this.database.transaction('rw', this.database.workoutExercises, this.database.workoutSessions, async () => {
      await this.database.workoutExercises.update(id, { notes: notes.trim() || null, updatedAt: timestamp });
      await this.database.workoutSessions.update(exercise.workoutSessionId, { updatedAt: timestamp });
    });
  }

  async moveWorkoutExercise(id: string, delta: number): Promise<void> {
    await this.database.transaction('rw', this.database.workoutExercises, async () => {
      const item = await this.database.workoutExercises.get(id);
      if (!item) return;
      const ordered = await this.database.workoutExercises.where('workoutSessionId').equals(item.workoutSessionId).sortBy('orderIndex');
      const index = ordered.findIndex(value => value.id === id);
      const other = ordered[index + delta];
      if (!other) return;
      const timestamp = nowIso();
      await Promise.all([
        this.database.workoutExercises.update(item.id, { orderIndex: other.orderIndex, updatedAt: timestamp }),
        this.database.workoutExercises.update(other.id, { orderIndex: item.orderIndex, updatedAt: timestamp })
      ]);
    });
  }

  async updateRestState(id: string, patch: Partial<Pick<WorkoutSession, 'restEndsAt' | 'restPausedSeconds' | 'restSelectedSeconds'>>): Promise<void> {
    await this.database.workoutSessions.update(id, { ...patch, updatedAt: nowIso() });
  }

  async finishWorkout(id: string, discardIncomplete: boolean): Promise<void> {
    await withWriteLock('active-workout', () => this.database.transaction(
      'rw', this.database.workoutSessions, this.database.workoutExercises, this.database.setEntries, this.database.scheduledWorkouts,
      async () => {
        const session = await this.database.workoutSessions.get(id);
        if (!session || session.status !== 'active') throw new UserFacingError('Тренировка уже завершена или удалена.');
        const exerciseIds = (await this.database.workoutExercises.where('workoutSessionId').equals(id).toArray()).map(item => item.id);
        const entries = exerciseIds.length > 0 ? await this.database.setEntries.where('workoutExerciseId').anyOf(exerciseIds).toArray() : [];
        if (!entries.some(item => item.isCompleted)) throw new UserFacingError('Завершите хотя бы один подход.');
        if (discardIncomplete) await this.database.setEntries.bulkDelete(entries.filter(item => !item.isCompleted).map(item => item.id));
        const timestamp = nowIso();
        await this.database.workoutSessions.update(id, {
          status: 'completed', finishedAt: timestamp, restEndsAt: null, restPausedSeconds: null, updatedAt: timestamp
        });
        const linked = await this.database.scheduledWorkouts.where('linkedWorkoutSessionId').equals(id).first();
        if (linked) await this.database.scheduledWorkouts.update(linked.id, { status: 'completed', updatedAt: timestamp });
      }
    ));
  }

  async deleteWorkout(id: string): Promise<void> {
    await withWriteLock('active-workout', () => this.database.transaction(
      'rw', this.database.workoutSessions, this.database.workoutExercises, this.database.setEntries, this.database.scheduledWorkouts,
      async () => {
        const exercises = await this.database.workoutExercises.where('workoutSessionId').equals(id).toArray();
        const setIds = exercises.length > 0
          ? (await this.database.setEntries.where('workoutExerciseId').anyOf(exercises.map(item => item.id)).toArray()).map(item => item.id)
          : [];
        const linked = await this.database.scheduledWorkouts.where('linkedWorkoutSessionId').equals(id).first();
        if (linked) await this.database.scheduledWorkouts.update(linked.id, {
          status: 'scheduled', linkedWorkoutSessionId: null, updatedAt: nowIso()
        });
        if (setIds.length > 0) await this.database.setEntries.bulkDelete(setIds);
        await this.database.workoutExercises.bulkDelete(exercises.map(item => item.id));
        await this.database.workoutSessions.delete(id);
      }
    ));
  }

  async updateWorkoutNotes(id: string, notes: string): Promise<void> {
    await this.database.workoutSessions.update(id, { notes: notes.trim() || null, updatedAt: nowIso() });
  }

  async updateWorkoutDate(id: string, workoutDate: LocalDate): Promise<void> {
    const today = toLocalDate(new Date());
    if (workoutDate > today) throw new UserFacingError('Завершённую тренировку нельзя перенести в будущее.');
    await this.database.workoutSessions.update(id, { workoutDate, updatedAt: nowIso() });
  }

  async schedules(includeCancelled = false): Promise<ScheduledWorkout[]> {
    const items = await this.database.scheduledWorkouts.orderBy('scheduledDate').toArray();
    return includeCancelled ? items : items.filter(item => item.status !== 'cancelled');
  }

  async schedulesOn(date: LocalDate): Promise<ScheduledWorkout[]> {
    return (await this.database.scheduledWorkouts.where('scheduledDate').equals(date).toArray())
      .filter(item => item.status !== 'cancelled')
      .sort((a, b) => a.reminderTime.localeCompare(b.reminderTime));
  }

  async saveSchedule(input: CreateScheduleInput): Promise<ScheduledWorkout> {
    const today = toLocalDate(new Date());
    if (compareLocalDates(input.scheduledDate, today) <= 0) throw new UserFacingError('Выберите будущую дату.');
    if (input.reminderTime && !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(input.reminderTime)) {
      throw new UserFacingError('Выберите корректное время.');
    }
    const plan = await this.database.workoutPlans.get(input.planId);
    if (!plan || plan.isArchived) throw new UserFacingError('План недоступен. Выберите другой план.');
    const timestamp = nowIso();
    const existing = input.id ? await this.database.scheduledWorkouts.get(input.id) : undefined;
    if (input.id && (!existing || existing.status !== 'scheduled')) throw new UserFacingError('Начатое расписание нельзя изменить.');
    const item: ScheduledWorkout = existing ? {
      ...existing, scheduledDate: input.scheduledDate, reminderTime: input.reminderTime,
      planId: plan.id, planNameSnapshot: plan.name, calendarExportedAt: null, updatedAt: timestamp
    } : {
      id: makeId(), scheduledDate: input.scheduledDate, reminderTime: input.reminderTime,
      planId: plan.id, planNameSnapshot: plan.name, status: 'scheduled', linkedWorkoutSessionId: null,
      calendarExportedAt: null, createdAt: timestamp, updatedAt: timestamp
    };
    await this.database.transaction('rw', this.database.scheduledWorkouts, this.database.appSettings, async () => {
      await this.database.scheduledWorkouts.put(item);
      if (input.reminderTime) await this.database.appSettings.update(SETTINGS_ID, {
        lastScheduledReminderTime: input.reminderTime, updatedAt: timestamp
      });
    });
    return item;
  }

  async cancelSchedule(id: string): Promise<void> {
    const item = await this.database.scheduledWorkouts.get(id);
    if (!item) return;
    await this.database.scheduledWorkouts.update(id, { status: 'cancelled', updatedAt: nowIso() });
  }

  async markCalendarExported(id: string): Promise<void> {
    await this.database.scheduledWorkouts.update(id, { calendarExportedAt: nowIso(), updatedAt: nowIso() });
  }

  async snapshot(): Promise<DatabaseSnapshot> {
    const [exercises, workoutPlans, planExercises, workoutSessions, workoutExercises, setEntries,
      scheduledWorkouts, appSettings, appMetadata, userProfiles, exercisePreferences, bodyMeasurements] = await Promise.all([
      this.database.exercises.toArray(), this.database.workoutPlans.toArray(), this.database.planExercises.toArray(),
      this.database.workoutSessions.toArray(), this.database.workoutExercises.toArray(), this.database.setEntries.toArray(),
      this.database.scheduledWorkouts.toArray(), this.database.appSettings.toArray(), this.database.appMetadata.toArray(),
      this.database.userProfiles.toArray(), this.database.exercisePreferences.toArray(), this.database.bodyMeasurements.toArray()
    ]);
    return { exercises, workoutPlans, planExercises, workoutSessions, workoutExercises, setEntries, scheduledWorkouts, appSettings, appMetadata, userProfiles, exercisePreferences, bodyMeasurements };
  }

  async mergeSnapshot(snapshot: DatabaseSnapshot): Promise<MergeReport> {
    return this.database.transaction('rw', this.database.tables, async () => {
      let added = 0;
      let updated = 0;
      let skipped = 0;
      for (const tableName of snapshotTableNames) {
        const table = this.database.table(tableName) as Table<{ id: string; updatedAt: string }, string>;
        for (const incoming of snapshot[tableName] as Array<{ id: string; updatedAt: string }>) {
          // Measurements and per-exercise settings have stable business keys as well as UUIDs.
          // Merging two copies made on separate devices must not violate those unique indexes.
          let current = await table.get(incoming.id);
          if (!current && tableName === 'bodyMeasurements') current = await this.database.bodyMeasurements.where('date').equals((incoming as import('../types').BodyMeasurement).date).first();
          if (!current && tableName === 'exercisePreferences') current = await this.database.exercisePreferences.where('exerciseId').equals((incoming as import('../types').ExercisePreference).exerciseId).first();
          if (!current) {
            await table.add(incoming);
            added += 1;
          } else if (incoming.updatedAt > current.updatedAt) {
            await table.put({ ...incoming, id: current.id });
            updated += 1;
          } else {
            skipped += 1;
          }
        }
      }
      databaseSnapshotSchema.parse(await this.snapshot());
      await this.database.appMetadata.update(METADATA_ID, { schemaVersion: DATABASE_SCHEMA_VERSION, appVersion: APP_VERSION, updatedAt: nowIso() });
      return { added, updated, skipped };
    });
  }

  private validatePlanDrafts(items: PlanExerciseDraft[]): void {
    for (const item of items) {
      const positive = [item.targetSets, item.targetRepsMin, item.targetRepsMax]
        .every(value => value === null || (Number.isInteger(value) && value > 0));
      const weightValid = item.targetWeightKg === null || (Number.isFinite(item.targetWeightKg) && item.targetWeightKg >= 0);
      if ((item.targetSets ?? 0) > 1000 || (item.targetRepsMin ?? 0) > 1_000_000 || (item.targetRepsMax ?? 0) > 1_000_000 || (item.targetWeightKg ?? 0) > 1_000_000) {
        throw new UserFacingError('Слишком большое значение цели. Проверьте количество подходов, повторений и вес.');
      }
      const rangeValid = item.targetRepsMin === null || item.targetRepsMax === null || item.targetRepsMin <= item.targetRepsMax;
      if (!positive || !weightValid || !rangeValid) throw new UserFacingError('Проверьте цели упражнений в плане.');
    }
  }

  async previousCompletedSets(exerciseId: string, currentSessionId: string): Promise<SetEntry[]> {
    const current = await this.database.workoutSessions.get(currentSessionId);
    if (!current) return [];
    const completed = (await this.database.workoutSessions.where('status').equals('completed').toArray())
      .filter(item => item.id !== current.id && (
        item.workoutDate < current.workoutDate || (item.workoutDate === current.workoutDate && item.createdAt < current.createdAt)
      ))
      .toSorted((a, b) => b.workoutDate.localeCompare(a.workoutDate) || b.createdAt.localeCompare(a.createdAt));
    for (const session of completed) {
      const exercise = (await this.database.workoutExercises.where('workoutSessionId').equals(session.id).toArray())
        .find(item => item.exerciseIdSnapshot === exerciseId);
      if (!exercise) continue;
      const sets = (await this.database.setEntries.where('workoutExerciseId').equals(exercise.id).sortBy('orderIndex'))
        .filter(item => item.isCompleted);
      if (sets.length > 0) return sets;
    }
    return [];
  }

  private async touchWorkoutExercise(exercise: WorkoutExercise, timestamp: string): Promise<void> {
    await Promise.all([
      this.database.workoutExercises.update(exercise.id, { updatedAt: timestamp }),
      this.database.workoutSessions.update(exercise.workoutSessionId, { updatedAt: timestamp })
    ]);
  }
}

export const trainingRepository = new TrainingRepository();
